import { buildColumnsFromSchema } from "@/lib/table";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useMemo } from "react";
import { ZodObject, ZodRawShape, ZodReadonly } from "zod";
import { SelectAllHeader } from "../components/SelectAllHeader";
import { SelectRowCell } from "../components/SelectRowCell";
import { COLUMN_SELECT_ID } from "@/constants";

export default function useColumns<TData extends { id: string }>({
  name,
  translationKey,
  schema,
  enableRowSelection = false,
  overrides,
  enableSelectAll = false,
}: {
  name: string;
  translationKey?: string;
  schema: ZodReadonly<ZodObject<ZodRawShape>>;
  enableRowSelection?: boolean;
  overrides?: Record<string, ColumnDef<TData, number>>;
  enableSelectAll?: boolean;
}): ColumnDef<TData>[] {
  return useMemo(() => {
    const columns = buildColumnsFromSchema<TData>(
      schema,
      translationKey ?? name,
      overrides,
    );

    if (!enableRowSelection) {
      return columns;
    }

    return [
      ...(enableRowSelection
        ? [
            {
              id: COLUMN_SELECT_ID,
              header: () => (enableSelectAll ? <SelectAllHeader /> : null),
              size: 30,
              minSize: 30,
              enableResizing: false,
              enableHiding: false,
              enableColumnFilter: false,
              enablePinning: false,
              enableSorting: false,
              accessorFn: (row: TData) => row,
              cell: ({ row }: { row: Row<TData> }) => (
                <SelectRowCell id={row.id} disabled={!row.getCanSelect()} />
              ),
              meta: {
                enableRowSelection,
                className: "",
              },
            },
          ]
        : []),
      ...columns,
    ];
  }, [
    schema,
    translationKey,
    name,
    overrides,
    enableRowSelection,
    enableSelectAll,
  ]);
}
